import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { get, post, put, remove, API_URL } from '../../utils/api';

// API URL from environment or default
// const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000/api';

const UnitManagement = ({ siteUuid, siteName, onRefresh }) => {
  const navigate = useNavigate();
  const [units, setUnits] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentUnit, setCurrentUnit] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create' or 'edit'
  const [selectedSiteUuid, setSelectedSiteUuid] = useState(siteUuid);
  const [selectedSiteName, setSelectedSiteName] = useState(siteName);
  
  // Form data
  const [formData, setFormData] = useState({
    unitNumber: '',
    unitId: '',
    displayName: '',
    hardwareId: '',
    status: 'ACTIVE',
    isPrepaid: false
  });

  // Load sites on component mount
  useEffect(() => {
    fetchSites();
  }, []);

  // Load units whenever selectedSiteUuid changes
  useEffect(() => {
    if (selectedSiteUuid) {
      fetchUnits();
    }
  }, [selectedSiteUuid]);

  // Update selected site when props change
  useEffect(() => {
    if (siteUuid && siteUuid !== selectedSiteUuid) {
      setSelectedSiteUuid(siteUuid);
      setSelectedSiteName(siteName);
    }
  }, [siteUuid, siteName]);

  // Fetch sites from the API
  const fetchSites = async () => {
    try {
      const response = await get(`${API_URL}/site/sites`);
      setSites(response.data);
    } catch (err) {
      // console.error('Error fetching sites:', err);
      setError('Failed to load sites. Please try again later.');
    }
  };

  // Fetch units from the API
  const fetchUnits = async () => {
    if (!selectedSiteUuid) return;
    
    setLoading(true);
    setError(null);
    try {
      const response = await get(`${API_URL}/site/units`, {
        params: { siteUuid: selectedSiteUuid }
      });
      setUnits(response.data);
    } catch (err) {
      // console.error('Error fetching units:', err);
      setError('Failed to load units. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Handle site selection change
  const handleSiteChange = (e) => {
    const uuid = e.target.value;
    const site = sites.find(s => s.uuid === uuid);
    setSelectedSiteUuid(uuid);
    setSelectedSiteName(site ? site.name : '');
  };

  // Use this function to initialize component with correct loading state
  useEffect(() => {
    // Set loading to false if no site is selected
    if (!selectedSiteUuid) {
      setLoading(false);
    }
  }, []);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  // Open modal for creating a new unit
  const handleCreate = () => {
    setFormData({
      unitNumber: '',
      unitId: '',
      displayName: '',
      hardwareId: '',
      status: 'ACTIVE',
      isPrepaid: false
    });
    setFormMode('create');
    setCurrentUnit(null);
    setIsModalOpen(true);
  };

  // Open modal for editing an existing unit
  const handleEdit = (unit) => {
    setFormData({
      unitNumber: unit.unitNumber,
      unitId: unit.unitId?.toString() || '',
      displayName: unit.displayName || '',
      hardwareId: unit.hardwareId?.toString() || '',
      status: unit.status,
      isPrepaid: unit.isPrepaid || false
    });
    setFormMode('edit');
    setCurrentUnit(unit);
    setIsModalOpen(true);
  };

  // Submit form to create or update a unit
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Format the data
      const data = {
        siteUuid: selectedSiteUuid,
        siteName: selectedSiteName,
        unitNumber: formData.unitNumber,
        unitId: parseInt(formData.unitId),
        displayName: formData.displayName,
        hardwareId: formData.hardwareId ? parseInt(formData.hardwareId) : null,
        status: formData.status,
        isPrepaid: formData.isPrepaid
      };
      
      if (formMode === 'edit' && currentUnit) {
        // Add UUID for updates
        data.uuid = currentUnit.uuid;
        
        // Update existing unit
        await put(`${API_URL}/site/units`, data);
      } else {
        // Create new unit
        await post(`${API_URL}/site/units`, data);
      }
      
      // Close modal and refresh data
      setIsModalOpen(false);
      fetchUnits();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error saving unit:', err);
      setError('Failed to save unit. ' + (err.response?.data?.message || 'Please try again later.'));
      // Close modal on error
      setIsModalOpen(false);
      // Show alert with error message
      alert('Failed to save unit: ' + (err.response?.data?.message || 'Please try again later.'));
    }
  };

  // Handle unit deletion
  const handleDelete = async (unit) => {
    if (!confirm(`Are you sure you want to delete unit ${unit.unitNumber}? This action cannot be undone.`)) {
      return;
    }
    
    try {
      await remove(`${API_URL}/site/units/${unit.uuid}`);
      
      // Refresh data
      fetchUnits();
      if (onRefresh) onRefresh();
    } catch (err) {
      // console.error('Error deleting unit:', err);
      alert('Failed to delete unit. Please try again later.');
    }
  };

  // Navigate to bulk create page
  const handleBulkCreate = () => {
    navigate(`/site-management/bulk-create-units?siteUuid=${selectedSiteUuid}&siteName=${encodeURIComponent(selectedSiteName)}`);
  };

  // Only show loading spinner when a site is selected and units are being loaded
  if (selectedSiteUuid && loading && units.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}
      
      {/* Site Selector */}
      <div className="mb-6">
        <label htmlFor="siteSelector" className="block text-sm font-medium text-gray-700 mb-1">
          Select Site
        </label>
        <select
          id="siteSelector"
          className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
          value={selectedSiteUuid || ''}
          onChange={handleSiteChange}
        >
          <option value="">-- Select a site --</option>
          {sites.map(site => (
            <option key={site.uuid} value={site.uuid}>
              {site.name} ({site.code})
            </option>
          ))}
        </select>
      </div>
      
      <div className="flex justify-between items-center mb-4">
        <div>
          <h2 className="text-xl font-semibold">Units</h2>
          <p className="text-gray-600">Managing units for site: {selectedSiteName || selectedSiteUuid}</p>
        </div>
        <div className="flex space-x-2">
          <button
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded"
            onClick={handleCreate}
            disabled={!selectedSiteUuid}
          >
            Add Unit
          </button>
          <button
            className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded"
            onClick={handleBulkCreate}
            disabled={!selectedSiteUuid}
          >
            Bulk Create Units
          </button>
        </div>
      </div>
      
      {!selectedSiteUuid && (
        <div className="py-8 text-center bg-gray-50 rounded-lg">
          <p className="text-gray-500">Please select a site from the dropdown to manage its units.</p>
        </div>
      )}
      
      {selectedSiteUuid && (
        <>
          {/* Units Table */}
          <div className="overflow-x-auto">
            <table className="min-w-full bg-white">
              <thead className="bg-gray-50">
                <tr>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Unit Number</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Unit ID</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Display Name</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Hardware ID</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Status</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Prepaid</th>
                  <th className="py-2 px-4 text-left text-sm font-medium text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {units.length === 0 ? (
                  <tr>
                    <td colSpan="7" className="py-4 px-4 text-center text-gray-500">
                      No units found for this site. Click "Add Unit" to create one.
                    </td>
                  </tr>
                ) : (
                  units.map((unit) => (
                    <tr key={unit.uuid} className="hover:bg-gray-50">
                      <td className="py-2 px-4">{unit.unitNumber}</td>
                      <td className="py-2 px-4">{unit.unitId}</td>
                      <td className="py-2 px-4">{unit.displayName}</td>
                      <td className="py-2 px-4">{unit.hardwareId || '-'}</td>
                      <td className="py-2 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          unit.status === 'ACTIVE' ? 'bg-green-100 text-green-800' :
                          unit.status === 'INACTIVE' ? 'bg-red-100 text-red-800' :
                          unit.status === 'VACANT' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {unit.status}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          unit.isPrepaid ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {unit.isPrepaid ? 'Yes' : 'No'}
                        </span>
                      </td>
                      <td className="py-2 px-4">
                        <div className="flex space-x-2">
                          <button
                            className="text-blue-500 hover:text-blue-700"
                            onClick={() => handleEdit(unit)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-red-500 hover:text-red-700"
                            onClick={() => handleDelete(unit)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
      
      {/* Unit Form Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 max-w-xl w-full">
            <h2 className="text-xl font-semibold mb-4">
              {formMode === 'create' ? 'Add Unit' : 'Edit Unit'}
            </h2>
            
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit Number <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="unitNumber"
                    value={formData.unitNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                    maxLength={128}
                  />
                  <p className="text-xs text-gray-500 mt-1">Unique identifier for the unit in this site</p>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit ID <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="unitId"
                    value={formData.unitId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Display Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="displayName"
                    value={formData.displayName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hardware ID
                  </label>
                  <input
                    type="number"
                    name="hardwareId"
                    value={formData.hardwareId}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded"
                    required
                  >
                    <option value="ACTIVE">Active</option>
                    <option value="INACTIVE">Inactive</option>
                    <option value="VACANT">Vacant</option>
                    <option value="MAINTENANCE">Maintenance</option>
                  </select>
                </div>
                
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-1">
                    <input
                      type="checkbox"
                      name="isPrepaid"
                      checked={formData.isPrepaid}
                      onChange={(e) => setFormData({
                        ...formData,
                        isPrepaid: e.target.checked
                      })}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded mr-2"
                    />
                    Is Prepaid
                  </label>
                  <p className="text-xs text-gray-500 mt-1">Check if this is a prepaid unit</p>
                </div>
              </div>
              
              <div className="flex justify-end space-x-3 mt-6">
                <button
                  type="button"
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-100"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
                >
                  {formMode === 'create' ? 'Create Unit' : 'Update Unit'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UnitManagement; 